import moment from 'moment'
import api from '../../../api'
import set from 'lodash/set'
import unset from 'lodash/unset'
import {getMaskedString} from '../../modules/helpers'
import {TApiSetup} from '../../../types/api'
import {useEffect, useState} from 'react'
import {useFormContext} from '../../modules/ModuleForm'
import {getErrorToast} from '../../helpers/toasts'

const escapeReg = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Удаляет из html целые абзацы (<p>...</p>) и/или голые токены
 * вида {visits/<field>:[...]} для всех полей из disabledFields.
 */
const stripDisabledFields = (html: string, schemeName: string, disabledFields: Set<string>) => {
    if (!disabledFields || !disabledFields.size) return html
    let out = html

    // 1) Убираем только токены
    disabledFields.forEach((field) => {
        const token = new RegExp(`\\{${escapeReg(schemeName)}\\/${escapeReg(field)}:[^}]+\\}`, 'gi')
        out = out.replace(token, '')
    })

    // 2) Удаляем ставшие пустыми параграфы/LI (после выкинутых токенов)
    const EMPTY = `(?:\\s|&nbsp;|<br\\s*\\/?>|<span[^>]*>\\s*<\\/span>|<strong[^>]*>\\s*<\\/strong>|<em[^>]*>\\s*<\\/em>)*`
    out = out.replace(new RegExp(`<p[^>]*>${EMPTY}<\\/p>\\s*`, 'gi'), '')
    out = out.replace(new RegExp(`<li[^>]*>${EMPTY}<\\/li>\\s*`, 'gi'), '')

    // 3) Нормализуем лишние <br>
    out = out.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>')

    return out
}

const cleanDocHtml = (
    markup: string,
    disabledFields?: Set<string>,
    options?: {dropEmptyColumns?: boolean; filledFields?: Set<string>}
): string => {
    const dropEmptyColumns = options?.dropEmptyColumns ?? false
    const filledFields = options?.filledFields
    const wrap = document.createElement('div')
    wrap.innerHTML = markup

    // блоки с data-keep-if="<article>" защищаются от очистки, если указанное поле заполнено,
    // но явное отключение печати самого блока (чекбокс printFields/data-section) имеет приоритет
    if (filledFields && filledFields.size) {
        wrap.querySelectorAll('[data-keep-if]').forEach((el) => {
            const dep = el.getAttribute('data-keep-if')
            const ownSection = el.getAttribute('data-section')
            if (ownSection && disabledFields?.has(ownSection)) return
            if (dep && filledFields.has(dep)) el.setAttribute('data-keep', 'true')
        })
    }

    const stripInv = (s: string) =>
        (s || '')
            .replace(/[\u00A0\u202F]/g, ' ')
            .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '')
            .replace(/\s+/g, ' ')
            .trim()

    const punctOnly = (s: string) => /^[\s\u00A0;.,:\/\\\-–—•|()\[\]{}"'`-]*$/u.test(stripInv(s))
    const plain = (el: Element) => stripInv(el.textContent || '')
    const isHeaderCell = (td: Element) => {
        const t = plain(td)
        const strong = td.querySelector('strong,b')
        return !!strong || /[:：]\s*$/.test(t)
    }

    const rowIsHeader = (tr: Element) => {
        const td = tr.querySelector('td,th')
        return !!td && isHeaderCell(td)
    }

    const extractAlias = (token: string): string | null => {
        const m = token.match(/^([^:]+):([A-Za-z0-9_]+)(?::[A-Za-z0-9_]+)?$/)
        if (!m) return null
        const path = m[1]
        const alias = path.split('/').pop() || ''
        return alias.replace(/_id$/i, '') || null
    }

    // 1) вырезаем выключенные плейсхолдеры и помечаем строки
    if (disabledFields && disabledFields.size) {
        const PLACEHOLDER_RE = /\{([^{}]+)\}/g
        const walker = document.createTreeWalker(wrap, NodeFilter.SHOW_TEXT, null)
        const nodes: Text[] = []
        while (walker.nextNode()) nodes.push(walker.currentNode as Text)
        for (const tn of nodes) {
            const orig = tn.nodeValue ?? ''
            if (!orig.includes('{')) continue
            let changed = false
            const replaced = orig.replace(PLACEHOLDER_RE, (full, inner) => {
                const alias = extractAlias(inner || '')
                if (alias && disabledFields.has(alias)) {
                    changed = true
                    const tr = tn.parentElement?.closest('tr') as HTMLTableRowElement | null
                    if (tr) tr.setAttribute('data-pe-hit', '1')
                    return ''
                }
                return full
            })
            if (changed) tn.nodeValue = replaced
        }
    }

    // 2) удаляем чисто-пунктуационные текстовые узлы (чтобы одиночные «;» не оставались)
    {
        const walker = document.createTreeWalker(wrap, NodeFilter.SHOW_TEXT, null)
        const nodes: Text[] = []
        while (walker.nextNode()) nodes.push(walker.currentNode as Text)
        for (const tn of nodes) {
            if (tn.parentElement?.closest('table[data-keep="true"]')) continue
            const s = stripInv(tn.nodeValue ?? '')
            if (s && punctOnly(s)) tn.nodeValue = ''
        }
    }

    // 2.5) удаляем элементы с data-section, совпадающим с disabledFields
    if (disabledFields && disabledFields.size) {
        wrap.querySelectorAll('[data-section]').forEach((el) => {
            const section = el.getAttribute('data-section')
            if (section && disabledFields.has(section)) el.remove()
        })
    }

    // 3) чистим таблицы
    Array.from(wrap.querySelectorAll('table')).forEach((table: HTMLTableElement) => {
        if (table.getAttribute('data-keep') === 'true') return
        const rows = Array.from(table.querySelectorAll('tr'))
        if (!rows.length) {
            table.remove()
            return
        }
        const colCount = Math.max(...rows.map((r) => r.querySelectorAll('td,th').length || 0), 0)

        if (colCount >= 2) {
            const allRows = Array.from(table.querySelectorAll('tr'))
            allRows.forEach((tr, idx) => {
                const cells = Array.from(tr.querySelectorAll('td,th'))
                if (cells.length < 2) return
                const hasPH = tr.innerHTML.includes('{')
                if (hasPH) return
                const hadPlaceholder = tr.getAttribute('data-pe-hit') === '1'
                const leftLooksHeader = cells.slice(0, -1).some((td) => isHeaderCell(td))
                if (leftLooksHeader || hadPlaceholder) {
                    // строка типа "Лейбл: значение" — удаляем если последняя ячейка пуста
                    const valueTd = cells[cells.length - 1]
                    const val = plain(valueTd)
                    const emptyLike = !val || punctOnly(val)
                    const isSectionHeader = idx === 0 && emptyLike && !hadPlaceholder
                    if (emptyLike && !isSectionHeader) tr.remove()
                } else {
                    // строка с данными — удаляем только если ВСЕ ячейки пусты
                    const allEmpty = cells.every((td) => {
                        const val = plain(td)
                        return !val || punctOnly(val)
                    })
                    if (allEmpty) tr.remove()
                }
            })
        } else {
            // одна колонка: «Заголовок:» строкой, значения — ниже
            const trList = Array.from(table.querySelectorAll('tr'))
            const rowIsHeader = (tr: Element) => {
                const td = tr.querySelector('td,th')
                return !!td && isHeaderCell(td)
            }
            const rowHasPH = (tr: Element) => tr.innerHTML.includes('{')

            let i = 0
            while (i < trList.length) {
                const headerTr = trList[i]
                if (!rowIsHeader(headerTr)) {
                    i++
                    continue
                }
                const toRemove: Element[] = []
                let k = i + 1
                let abort = false
                while (k < trList.length) {
                    const candidate = trList[k]
                    if (rowIsHeader(candidate)) break
                    if (rowHasPH(candidate)) {
                        abort = true
                        break
                    }
                    const txt = plain(candidate)
                    if (!txt || punctOnly(txt)) {
                        toRemove.push(candidate)
                        k++
                        continue
                    }
                    abort = true
                    break
                }
                if (!abort) {
                    headerTr.remove()
                    toRemove.forEach((tr) => tr.remove())
                }
                i = k
            }
        }

        // если нет данных — сносим
        const left = Array.from(table.querySelectorAll('tr'))
        if (!left.length) {
            table.remove()
            return
        }

        // если осталась только «шапка»
        if (left.length === 1) {
            const cells = Array.from(left[0].querySelectorAll('td,th'))
            if (cells.length && cells.every((td) => isHeaderCell(td) || !plain(td))) {
                table.remove()
                return
            }
        }

        // таблица без видимого текста
        if (
            !table.querySelector('td,th') ||
            (plain(table) === '' && !table.querySelector('img,svg'))
        ) {
            table.remove()
        }
    })

    if (dropEmptyColumns) {
        Array.from(wrap.querySelectorAll('table')).forEach((table: HTMLTableElement) => {
            const rows = Array.from(table.querySelectorAll('tr'))
            if (!rows.length) return

            const maxCols = Math.max(...rows.map((r) => r.querySelectorAll('td,th').length || 0), 0)
            if (!maxCols) return

            const colsHaveData: boolean[] = new Array(maxCols).fill(false)

            rows.forEach((tr) => {
                const cells = Array.from(tr.querySelectorAll('td,th'))
                const inThead = tr.closest('thead') !== null
                const headerLike = rowIsHeader(tr)

                cells.forEach((cell, colIdx) => {
                    // Считаем только "данные", а не заголовки
                    if (inThead || headerLike) return

                    const hasPH = cell.innerHTML.includes('{')
                    const txt = plain(cell)
                    const hasMedia = !!cell.querySelector('img,svg,canvas,video,iframe')
                    const meaningful = hasPH || hasMedia || (!!txt && !punctOnly(txt))

                    if (meaningful) colsHaveData[colIdx] = true
                })
            })

            const colsToDrop = colsHaveData
                .map((keep, idx) => (!keep ? idx : -1))
                .filter((idx) => idx >= 0)

            if (!colsToDrop.length) return

            // Удаляем ячейки по индексам (с конца, чтобы индексы не съезжали)
            rows.forEach((tr) => {
                const cells = Array.from(tr.querySelectorAll('td,th'))
                colsToDrop
                    .slice()
                    .reverse()
                    .forEach((idx) => {
                        if (cells[idx]) cells[idx].remove()
                    })
            })

            // Если после выкидывания таблица совсем развалилась — проверим, есть ли вообще ячейки
            const tdthLeft = table.querySelectorAll('td,th')
            if (!tdthLeft.length) {
                table.remove()
            }
        })
    }

    // 4) удалить пустые контейнеры без плейсхолдеров
    wrap.querySelectorAll('td, p, div, li, section, article, tr, table').forEach((el) => {
        if (el.closest('table[data-keep="true"]')) return
        if (el.innerHTML.includes('{')) return
        if (el.querySelector('img,svg,canvas,video,iframe,br')) return
        if (!plain(el)) el.remove()
    })

    // 5) убрать «шапочные» пустые таблицы (thead есть, tbody пуст) + заголовок над ними
    const isHeadingTag = (el: Element | null) => !!el && /^(p|h1|h2|h3|h4|h5|h6)$/i.test(el.tagName)
    Array.from(wrap.querySelectorAll('table')).forEach((tbl: HTMLTableElement) => {
        const bodyRows =
            (tbl.tBodies && Array.from(tbl.tBodies).reduce((n, tb) => n + tb.rows.length, 0)) ||
            tbl.querySelectorAll('tbody tr').length
        const headRows =
            (tbl.tHead && tbl.tHead.rows.length) || tbl.querySelectorAll('thead tr').length
        if (headRows > 0 && bodyRows === 0) {
            const prev = tbl.previousElementSibling
            if (isHeadingTag(prev)) {
                const txt = plain(prev!)
                if (txt && !/\{[^}]+\}/.test(prev!.innerHTML)) prev!.remove()
            }
            tbl.remove()
        }
    })

    // 6) подчистить инлайновые height/min/max-height
    const els = wrap.querySelectorAll('table, tr, td, p, div, section, article')
    els.forEach((el) => {
        const node = el as HTMLElement
        node.style.removeProperty('height')
        node.style.removeProperty('min-height')
        node.style.removeProperty('max-height')
        const s = node.getAttribute('style')
        if (s) {
            const cleaned = s
                .replace(/(?:^|;)\s*height\s*:\s*[^;]+;?/gi, '')
                .replace(/(?:^|;)\s*min-height\s*:\s*[^;]+;?/gi, '')
                .replace(/(?:^|;)\s*max-height\s*:\s*[^;]+;?/gi, '')
                .replace(/^\s*;|\s*;$/g, '')
            if (cleaned.trim()) node.setAttribute('style', cleaned)
            else node.removeAttribute('style')
        }
    })

    return wrap.innerHTML
}

const cleanBlackListMarks = (html: string) =>
    html
        .replace(/(?:[\s\u00A0\u202F])*!\((?=never)\)/g, '')
        .replace(/(?:[\s\u00A0\u202F])*\(\!\)(?:[\s\u00A0\u202F])*/g, ' ')
        .replace(/\s{2,}/g, ' ')

//функция вызова хука у поля после изменения значений (формы)
export const formHookAfterChange = async (
    field_article: string,
    field_value: any,
    values: any,
    setFieldValue: (article: string, value: any) => void,
    requestObject: string,
    setIsFormPreparing: (state: boolean) => void
) => {
    try {
        setIsFormPreparing(true)
        //технические данные в форме
        const fieldsVisibilityArticle = 'fieldsVisibility'
        const fieldsDescriptionsArticle = 'fieldsDescriptions'
        const fieldsDisabledArticle = 'fieldsDisabled'
        //

        //использую lodash по причине наличия вложенности во многих артикулах
        const context = {trigger: field_article}
        const valuesWithCurrentFieldValue = set({...values, context}, field_article, field_value)
        unset(valuesWithCurrentFieldValue, fieldsVisibilityArticle)
        unset(valuesWithCurrentFieldValue, fieldsDescriptionsArticle)
        unset(valuesWithCurrentFieldValue, fieldsDisabledArticle)

        const {data: actualValues} = await api(requestObject, 'hook', valuesWithCurrentFieldValue)
        if (actualValues && !Array.isArray(actualValues)) {
            for (let key in actualValues) {
                const fieldSettings = actualValues[key]

                if (key === 'modal_info') {
                    setFieldValue(key, fieldSettings)
                    continue
                }

                // if (Array.isArray(fieldSettings?.value) && Array.isArray(values[key])) {
                //     const idx = getIndexFromTrigger(valuesWithCurrentFieldValue?.context?.trigger)
                //     if (idx !== null) {
                //         const next = [...values[key]]
                //         const patchRow = fieldSettings.value[idx] ?? fieldSettings.value[0]
                //         if (patchRow) {
                //             const fixedPatchRow = {
                //                 ...patchRow,
                //                 ...(Array.isArray(patchRow.row_id) && patchRow?.row_id.length === 1
                //                     ? {row_id: patchRow.row_id[0]}
                //                     : {})
                //             }
                //             next[idx] = { ...next[idx], ...fixedPatchRow }
                //             setFieldValue(key, next)
                //             continue
                //         }
                //     }
                //     setFieldValue(key, fieldSettings.value)
                //     continue
                // }

                if (fieldSettings.hasOwnProperty('value') && key !== field_article) {
                    setFieldValue(key, fieldSettings.value)
                }
                if (fieldSettings.hasOwnProperty('is_visible')) {
                    setFieldValue(`${fieldsVisibilityArticle}.${key}`, fieldSettings.is_visible)
                    if (field_value) {
                        setFieldValue(`printFields.${key.replace(/_id$/i, '')}`, fieldSettings.is_visible)
                    }
                }
                if (fieldSettings.hasOwnProperty('is_disabled')) {
                    setFieldValue(`${fieldsDisabledArticle}.${key}`, fieldSettings.is_disabled)
                }
                if (fieldSettings.hasOwnProperty('description')) {
                    setFieldValue(`${fieldsDescriptionsArticle}.${key}`, fieldSettings.description)
                }
            }

            /*
            --- модальное окно уведомлений
            */
            if ('modal_info' in actualValues) {
                setFieldValue('modal_info', actualValues['modal_info'])
            } else {
                setFieldValue('modal_info', null)
            }
        }
    } catch (error: any) {
        getErrorToast(`${error}`)
    } finally {
        setIsFormPreparing(false)
    }
}
//функция вызова хука у поля после изменения значений (фильтры)
const filtersHookAfterChange = async (
    field_article: string,
    field_value: any,
    values: any,
    setFieldValue: (article: string, value: any) => void,
    requestObject: string,
    handleSubmit?: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
) => {
    //технические данные в форме
    const fieldsVisibilityArticle = 'fieldsVisibility'
    const fieldsDescriptionsArticle = 'fieldsDescriptions'
    const fieldsDisabledArticle = 'fieldsDisabled'
    //

    //использую lodash по причине наличия вложенности во многих артикулах
    const context = {trigger: field_article}
    const valuesWithCurrentFieldValue = set({...values, context}, field_article, field_value)
    unset(valuesWithCurrentFieldValue, fieldsVisibilityArticle)
    unset(valuesWithCurrentFieldValue, fieldsDescriptionsArticle)
    unset(valuesWithCurrentFieldValue, fieldsDisabledArticle)

    try {
        const {data: actualValues} = await api(
            requestObject,
            'hook_filters',
            valuesWithCurrentFieldValue
        )
        if (actualValues && !Array.isArray(actualValues)) {
            for (let key in actualValues) {
                const fieldSettings = actualValues[key]

                if (fieldSettings.hasOwnProperty('value') && key !== field_article) {
                    setFieldValue(key, fieldSettings.value)
                }
                if (fieldSettings.hasOwnProperty('is_visible')) {
                    setFieldValue(`${fieldsVisibilityArticle}.${key}`, fieldSettings.is_visible)
                    if (field_value) {
                        setFieldValue(`printFields.${key.replace(/_id$/i, '')}`, fieldSettings.is_visible)
                    }
                    if (fieldSettings.is_visible === false) {
                        setFieldValue(key, undefined)
                    }
                }
                /*    if (fieldSettings.hasOwnProperty("is_disabled")) {
                       setFieldValue(`${fieldsDisabledArticle}.${key}`, fieldSettings.is_disabled)
                   }
                   if (fieldSettings.hasOwnProperty("description")) {
                       setFieldValue(`${fieldsDescriptionsArticle}.${key}`, fieldSettings.description)
                   } */
            }
        }
        /* почему-то моментальный запрос не даёт возможности изменить поля через вышеуказанный цикл */
        setTimeout(() => handleSubmit?.(), 1)
    } catch (error: any) {
        getErrorToast(`${error}`)
    }
}

/* полям фильтров, у которых хук находится в change, не передавать handleSubmit */
export const useHook = ({
    article,
    setFieldValue,
    handleSubmit,
    values,
    isFilter,
    hook,
}: {
    article: string
    values: any
    setFieldValue: (article: string, value: any) => void
    handleSubmit?: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
    hook?: string
    isFilter?: boolean
}) => {
    const [valueForHook, setValueForHook] = useState<any>(undefined)
    const [isInitialized, setIsInitialized] = useState(false)
    const {isFormPreparing, setIsFormPreparing} = useFormContext()

    /* Для фильтров. При зачистке фильтров сбрасывать значение valueForHook, т.к. иначе, при выборе того же значения, которое было до зачистки, хук не вызовется */
    useEffect(() => {
        if (valueForHook !== undefined && values?.[article] === undefined) {
            setValueForHook(undefined)
        }
    }, [values?.[article]])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setIsInitialized(true)
        }, 500)
        return () => clearTimeout(timeout)
    }, [])

    useEffect(() => {
        if (!isInitialized || !hook) return
        if (isFormPreparing) return
        const isValidValue = valueForHook !== null && valueForHook !== undefined
        if (hook) {
            if (isFilter && valueForHook !== undefined) {
                filtersHookAfterChange(
                    article,
                    valueForHook,
                    values,
                    setFieldValue,
                    hook,
                    handleSubmit
                )
            } else {
                formHookAfterChange(
                    article,
                    valueForHook,
                    values,
                    setFieldValue,
                    hook,
                    setIsFormPreparing
                )
            }
        }
    }, [valueForHook, isFormPreparing])

    return setValueForHook
}
//функции для автоподстановки переменных в документ
const getFormattedValue = (type: string, value: any, context: TApiSetup) => {
    const isPrinting = (context as any)?.isPrinting === true
    const emptyOut = isPrinting ? '' : '-'
    if (value === null || value === undefined || value === '') {
        return emptyOut
    } else {
        switch (type) {
            case 'date':
                return moment(value).format('DD.MM.YYYY г.')
            case 'time':
                return moment(value, 'HH:mm').format('HH:mm')
            case 'datetime':
                /*
                --- может быть формат типа гггг-мм-дд чч:мм - чч:мм
                */
                const splitedTime = value.replace(' - ', '-').split(' ')[1].split('-')
                const isTimeSpectr = splitedTime.length === 2
                return isTimeSpectr
                    ? `${moment(value.split(' ')[0]).format('DD.MM.YYYY')} ${splitedTime[0]} - ${
                          splitedTime[1]
                      }`
                    : moment(value).format('DD.MM.YYYY HH:mm')
            case 'price':
            case 'phone':
                return getMaskedString(value, type, context)
            case 'list':
                return Array.isArray(value)
                    ? value.reduce(
                          (acc, item, index, arr) =>
                              acc + `${item?.title ?? item}${index < arr.length - 1 ? ', ' : ''}`,
                          ''
                      )
                    : value?.title ?? value ?? emptyOut
            case 'textareaOptions':
                return typeof value === 'string' ? value.replace(/\n/g, '<br>') : value
            default:
                return value
        }
    }
}

const addDisabled = (setRef: Set<string>, name: string) => {
    const base = String(name)
    setRef.add(base.replace(/_id$/i, '')) // complaints → complaints
    setRef.add(base.endsWith('_id') ? base : `${base}_id`) // complaints → complaints_id
}

export const autocompleteDocument = async (
    document: {
        object?: string
        structure: Array<{block_type: string; settings: {document_body: string}}>
    },
    row_id: string | number,
    scheme_name: string,
    currentUser: any,
    applicationContext: TApiSetup,
    localValues?: Record<string, any>
) => {
    const variables: any = {
        globalVariables: {
            id: currentUser.id,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            patronymic: currentUser.patronymic,
            fio: `${currentUser.last_name ?? '-'} ${currentUser.first_name?.[0] ?? '-'}. ${
                currentUser.patronymic?.[0] ?? '-'
            }.`,
            today: moment().format('YYYY-MM-DD HH:mm:ss'),
        },
    }

    //если привязан объект и передан id строки объекта - сделать запрос на получение значений объекта
    if (scheme_name && row_id) {
        const {data: variableValues} = await api('admin', 'get-variables-value', {
            scheme_name,
            row_id,
        })
        variables[scheme_name] = variableValues
    }

    // поля объекта, которые фактически заполнены - для data-keep-if в шаблоне
    const filledFields = new Set<string>()
    const schemeVariables = variables[scheme_name]
    if (schemeVariables && typeof schemeVariables === 'object') {
        Object.entries(schemeVariables).forEach(([key, val]) => {
            const isFilled = Array.isArray(val)
                ? val.length > 0
                : val && typeof val === 'object'
                ? 'value' in (val as any)
                    ? Boolean((val as any).value)
                    : Object.values(val as object).some((v) => v !== null && v !== undefined && v !== '')
                : Boolean(val)
            if (isFilled) filledFields.add(key)
        })
    }

    //сложение структуры документа в одну строку
    let documentContent = document.structure
        .filter((block) => !block.block_type || block.block_type === 'text')
        .reduce((acc, block) => acc + block.settings.document_body + '<br>', '')

    // === НОВОЕ: применяем чекбоксы printParagraphs ===
    // Ожидаем: localValues.printParagraphs: { "-complaints-diseaseAnamnesis-...": true|false, ... }
    const disabledFields = new Set<string>()
    const printMap = localValues?.printParagraphs
    const blockFieldsMap = localValues?.printBlockFields as Record<string, string[]> | undefined

    if (printMap && typeof printMap === 'object') {
        Object.entries(printMap).forEach(([blockKey, isEnabled]) => {
            if (isEnabled === false) {
                const fromKey = blockKey.split('-').filter(Boolean)
                const fromMap = blockFieldsMap?.[blockKey] ?? []
                const merged = new Set<string>([...fromKey, ...fromMap])
                merged.forEach((f) => addDisabled(disabledFields, f))
            }
        })
    }

    const printFields = localValues?.printFields
    if (printFields && typeof printFields === 'object') {
        const walk = (obj: any) => {
            Object.entries(obj).forEach(([k, v]) => {
                if (v && typeof v === 'object') {
                    walk(v) // вложенные ветки
                } else if (v === false) {
                    addDisabled(disabledFields, String(k))
                }
            })
        }
        walk(printFields)
    }

    if (disabledFields.size) {
        documentContent = cleanDocHtml(documentContent, disabledFields, {
            dropEmptyColumns: applicationContext.isClear,
            filledFields,
        })
        documentContent = stripDisabledFields(documentContent, scheme_name, disabledFields)
    }

    //проверка на наличие динамических строк таблицы, заполняется перед переменными, чтобы нижний парсер не задел переменные таблицы
    const dynamicTableRows = documentContent.match(
        /<tr.+data-dynamic="true">((?!<\/tr>)[\s\S])*<\/tr>/gi
    )
    if (Array.isArray(dynamicTableRows)) {
        const resolvedTableRows = dynamicTableRows.map((row) => {
            //находим переменные в строке
            const variablesFromRow = row.match(/{[^}]+}/gi)
            //находим ключ, значения которого будут перечисляться в таблице. Ключ находится в атрибуте data-type (data-type="some prop")
            const sourceKey =
                row
                    .match(/data-type="\w+"/gi)?.[0]
                    .split('="')[1]
                    .slice(0, -1) ?? ''
            const sourceKeyValue = variables[scheme_name]?.[sourceKey]
            //проверяем, найден ли источник данных, а так же то, является ли он массивом или объектом
            const resolvedValue =
                sourceKeyValue && Array.isArray(variablesFromRow)
                    ? Array.isArray(sourceKeyValue)
                        ? sourceKeyValue.reduce((acc, item: any, index) => {
                              //определяем каждую переменную и заменяем в строке. Кол-во строк определяется длиной источника данных (массив)
                              const resolvedRow = variablesFromRow.reduce(
                                  (acc, variable: string) => {
                                      if (variable === '{index}') {
                                          return acc.replace(variable, `${index + 1}`)
                                      }
                                      const [path, parentType, article, type] = variable
                                          .replace(/{|}/gi, '')
                                          .split(':')
                                      const currentSourceKey = path.split('/')[1]
                                      if (currentSourceKey === sourceKey) {
                                          const resolvedValue = getFormattedValue(
                                              type,
                                              item[article],
                                              applicationContext
                                          )
                                          return acc.replace(variable, resolvedValue)
                                      } else {
                                          const currentValueSource =
                                              variables[scheme_name][currentSourceKey]
                                          const resolvedValue = Array.isArray(currentValueSource)
                                              ? currentValueSource
                                                    .map((item) =>
                                                        getFormattedValue(
                                                            type,
                                                            item?.[article],
                                                            applicationContext
                                                        )
                                                    )
                                                    .join('<br/>')
                                              : getFormattedValue(
                                                    type,
                                                    currentValueSource?.[article],
                                                    applicationContext
                                                )
                                          return acc.replace(variable, resolvedValue)
                                      }
                                  },
                                  row
                              )
                              const cleanedRow = resolvedRow
                                  .replace(/\s+data-dynamic="[^"]*"/gi, '')
                                  .replace(/\s+data-type="[^"]*"/gi, '')
                              return acc + cleanedRow
                          }, '')
                        : variablesFromRow.reduce((acc, variable: string) => {
                              if (variable === '{index}') {
                                  return acc.replace(variable, '1')
                              }
                              //определяем каждую переменную и заменяем в строке.
                              const [path, parentType, article, type] = variable
                                  .replace(/{|}/gi, '')
                                  .split(':')
                              const currentSourceKey = path.split('/')[1]
                              if (currentSourceKey === sourceKey) {
                                  const resolvedValue = getFormattedValue(
                                      type,
                                      sourceKeyValue[article],
                                      applicationContext
                                  )
                                  return acc.replace(variable, resolvedValue)
                              } else {
                                  const currentValueSource =
                                      variables[scheme_name][currentSourceKey]
                                  const resolvedValue = Array.isArray(currentValueSource)
                                      ? currentValueSource
                                            .map((item) =>
                                                getFormattedValue(
                                                    type,
                                                    item[article],
                                                    applicationContext
                                                )
                                            )
                                            .join('<br/>')
                                      : getFormattedValue(
                                            type,
                                            currentValueSource[article],
                                            applicationContext
                                        )
                                  return acc.replace(variable, resolvedValue)
                              }
                          }, row)
                    : row
            return {
                source: row,
                value: resolvedValue,
            }
        })
        documentContent = resolvedTableRows.reduce(
            (acc, row) => acc.replace(row.source, row.value),
            documentContent
        )
    }

    //поиск переменных в строке
    const variablesFromDocument = documentContent.match(/{[^}]+}/gi)
    //нахождение значений переменных из доступных в объекте variables данных и подстановка
    if (Array.isArray(variablesFromDocument)) {
        const resolved = variablesFromDocument.map((variable) => {
            if (variable === '{index}') return {source: variable, value: variable}
            /* данные по внутренней переменной могут отсутствовать */
            const [variablePath, variableType, innerVariablePath, innerVariableType] = variable
                .replace(/{|}/gi, '')
                .split(':')
            /*
            --- костылище
            */
            const value = variablePath.split('/').reduce((acc, path, index) => {
                const v = acc?.[path]
                if (!v && index === 0) {
                    const first = Object.entries(acc ?? {}).find(([k]) => k !== 'globalVariables')
                    return first ? first[1] : undefined
                }
                return v
            }, variables as any) /* ??
            /* при наличии внутреннего пути нужно проверить, массив ли это, т.к. объекты тоже передаются с типом list */
            const resolvedValue =
                innerVariablePath && value
                    ? Array.isArray(value)
                        ? value
                              .map((item: any) =>
                                  getFormattedValue(
                                      innerVariableType,
                                      item[innerVariablePath],
                                      applicationContext
                                  )
                              )
                              .reduce(
                                  (acc: string, step: any, index: number) =>
                                      acc + step + (index === value.length - 1 ? '' : ', '),
                                  ''
                              )
                        : getFormattedValue(
                              innerVariableType,
                              value[innerVariablePath],
                              applicationContext
                          )
                    : getFormattedValue(variableType, value, applicationContext)
            return {
                source: variable,
                value: resolvedValue,
            }
        })
        const resolvedDocument = resolved.reduce(
            (acc, variable) => acc.replace(variable.source, variable.value ?? variable.source),
            documentContent
        )

        const withoutBangMarks = cleanBlackListMarks(resolvedDocument)

        if (disabledFields.size > 0 || applicationContext.isClear) {
            return cleanDocHtml(
                withoutBangMarks,
                disabledFields.size ? disabledFields : undefined,
                {dropEmptyColumns: applicationContext.isClear, filledFields}
            )
        }

        return withoutBangMarks
    } else {
        return documentContent
    }
}
